import { Link } from "@tanstack/react-router"
import { Input } from "design-system/atoms/Input"
import { Textarea } from "design-system/atoms/Textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import InstagramAccountFormSelector from "modules/campaigns/components/Sections/triggers/instagram/InstagramAccountFormSelector"
import { UpdateCampaignSchemaFrontend } from "modules/campaigns/hooks/useCampaignForm"
import { useFormContext } from "react-hook-form"
import { Trans, useTranslation } from "react-i18next"
import { InstagramVerifiedOptions } from "schema"
import { z } from "zod"

type Props = {
  isDisabled: boolean
}

type FormValues = z.infer<typeof UpdateCampaignSchemaFrontend>
type IgDm = Extract<
  NonNullable<FormValues["triggerIntegration"]>,
  { type: "instagramDirectMessage" }
>

export function InstagramDirectMessageForm({ isDisabled }: Props) {
  const { t } = useTranslation()
  const form = useFormContext<FormValues>()
  const triggerIntegration = form.watch("triggerIntegration")
  const igDm =
    triggerIntegration?.type === "instagramDirectMessage" ? triggerIntegration : undefined

  // Rebuild the full IG-DM variant on every edit so `type` and the account id survive partial changes.
  const buildIgDm = (patch: Partial<IgDm>): IgDm => ({
    type: "instagramDirectMessage",
    instagramIntegrationId: igDm?.instagramIntegrationId ?? "",
    audience: igDm?.audience ?? undefined,
    delayInDays: igDm?.delayInDays ?? undefined,
    discardPhrases: igDm?.discardPhrases ?? [],
    ...patch,
  })

  return (
    <>
      <FormField
        control={form.control}
        name="triggerIntegration"
        render={({ field }) => (
          <div>
            <FormItem className="flex flex-col gap-2">
              <FormLabel>
                {t("campaigns.trigger.metadata.instagramDirectMessage.selectAccount")}
              </FormLabel>
              <FormControl>
                <InstagramAccountFormSelector
                  value={igDm?.instagramIntegrationId}
                  emptyPlaceholder={t(
                    "campaigns.trigger.metadata.instagramDirectMessage.emptyPlaceholder",
                  )}
                  placeholder={t(
                    "campaigns.trigger.metadata.instagramDirectMessage.emptyPlaceholder",
                  )}
                  searchPlaceholder={t(
                    "campaigns.trigger.metadata.instagramDirectMessage.searchPlaceholder",
                  )}
                  onChange={(account) => {
                    field.onChange(
                      account ? buildIgDm({ instagramIntegrationId: account.value }) : null,
                      { shouldDirty: true },
                    )
                  }}
                  hasCreateNew
                  isDisabled={isDisabled}
                />
              </FormControl>
              <FormMessage />
              <span className="text-sm text-muted-foreground">
                <Trans
                  i18nKey={"campaigns.trigger.metadata.instagramDirectMessage.accountDescription"}
                  components={{
                    a: (
                      <Link
                        className="underline text-muted-foreground"
                        to="/settings/channels/instagram"
                        target="_blank"
                      />
                    ),
                  }}
                />
              </span>
            </FormItem>
          </div>
        )}
      />
      <FormField
        control={form.control}
        name="triggerIntegration"
        render={({ field }) => (
          <div>
            <FormItem className="flex flex-col gap-2">
              <FormLabel>
                {t("campaigns.trigger.metadata.instagramDirectMessage.audience.title")}
              </FormLabel>
              <FormControl>
                <div className="flex flex-col gap-2">
                  <div>
                    <span className="text-muted-foreground text-xs">
                      {t(
                        "campaigns.trigger.metadata.instagramDirectMessage.audience.verified.title",
                      )}
                    </span>
                    <Select
                      value={igDm?.audience?.verified ?? undefined}
                      onValueChange={(value) => {
                        field.onChange(
                          buildIgDm({
                            audience: {
                              ...igDm?.audience,
                              verified: value as z.infer<typeof InstagramVerifiedOptions>,
                            },
                          }),
                          { shouldDirty: true },
                        )
                      }}
                      disabled={isDisabled}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {InstagramVerifiedOptions.options.map((option) => (
                          <SelectItem value={option} key={option}>
                            {t(
                              `campaigns.trigger.metadata.instagramDirectMessage.audience.verified.options.${option}`,
                            )}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-muted-foreground text-xs">
                        {t(
                          "campaigns.trigger.metadata.instagramDirectMessage.audience.minFollowersCount",
                        )}
                      </span>
                      <Input
                        value={igDm?.audience?.minFollowersCount ?? undefined}
                        min={0}
                        max={igDm?.audience?.maxFollowersCount ?? undefined}
                        type="number"
                        disabled={isDisabled}
                        onChange={(value) => {
                          field.onChange(
                            buildIgDm({
                              audience: {
                                ...igDm?.audience,
                                minFollowersCount: Number(value.target.value),
                              },
                            }),
                            { shouldDirty: true },
                          )
                        }}
                      />
                    </div>
                    <div>
                      <span className="text-muted-foreground text-xs">
                        {t(
                          "campaigns.trigger.metadata.instagramDirectMessage.audience.maxFollowersCount",
                        )}
                      </span>
                      <Input
                        value={igDm?.audience?.maxFollowersCount ?? undefined}
                        min={igDm?.audience?.minFollowersCount ?? 0}
                        type="number"
                        disabled={isDisabled}
                        onChange={(value) => {
                          field.onChange(
                            buildIgDm({
                              audience: {
                                ...igDm?.audience,
                                maxFollowersCount: Number(value.target.value),
                              },
                            }),
                            { shouldDirty: true },
                          )
                        }}
                      />
                    </div>
                  </div>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          </div>
        )}
      />
      <FormField
        control={form.control}
        name="triggerIntegration"
        render={({ field }) => (
          <FormItem className="flex flex-col gap-2">
            <FormLabel>
              {t("campaigns.trigger.metadata.instagramDirectMessage.discardPhrases.title")}
            </FormLabel>
            <FormControl>
              <Textarea
                value={igDm?.discardPhrases?.join("\n") ?? ""}
                placeholder={t(
                  "campaigns.trigger.metadata.instagramDirectMessage.discardPhrases.placeholder",
                )}
                disabled={isDisabled}
                onChange={(event) =>
                  field.onChange(
                    buildIgDm({
                      discardPhrases: event.target.value.split("\n"),
                    }),
                    { shouldDirty: true },
                  )
                }
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="triggerIntegration"
        render={({ field }) => (
          <div>
            <FormItem className="flex flex-col gap-1">
              <FormLabel>
                {t(
                  "campaigns.trigger.metadata.instagramDirectMessage.disableAfterHumanInteraction.title",
                )}
              </FormLabel>
              <FormControl>
                <div className="flex items-start gap-3">
                  <span className="text-muted-foreground text-xs block flex-1">
                    {t(
                      "campaigns.trigger.metadata.instagramDirectMessage.disableAfterHumanInteraction.description",
                    )}
                  </span>
                  <Input
                    wrapperClassName="w-auto"
                    className="w-24 shrink-0"
                    disabled={isDisabled}
                    value={igDm?.delayInDays ?? ""}
                    min={1}
                    type="number"
                    placeholder={t(
                      "campaigns.trigger.metadata.instagramDirectMessage.disableAfterHumanInteraction.placeholder",
                    )}
                    onChange={(value) => {
                      const delayInDays = value.target.value
                        ? Number(value.target.value)
                        : undefined

                      field.onChange(buildIgDm({ delayInDays }), { shouldDirty: true })
                    }}
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          </div>
        )}
      />
    </>
  )
}
